import { userAtom } from '@/domains/appShell/stores/user.store'
import { pagePermissionsAtom } from '@/shared/hooks/permissions/pagePermissions.store'

import { useCallback, useEffect } from 'react'

import { useAtom, useAtomValue } from 'jotai'

import { permissionsSnapshotAtom } from './permissions.store'

/**
 * Entry for a user-level permission (from the original user snapshot).
 */
export type UserPermissionEntry = {
  /** The raw permission string (e.g. `subscription:read`) */
  permission: string
  /** Whether this permission is currently active in `userAtom.permissions[]` */
  active: boolean
}

/**
 * Entry for a page-level permission (declared via `usePagePermissions`).
 */
export type PagePermissionEntry = {
  /** Logical camelCase key declared in the page (e.g. `canViewSubscriptions`) */
  key: string
  /** The raw permission string checked against `user.permissions[]` */
  permission: string
  /** Description provided in the `usePagePermissions` definition */
  description: string
  /** Whether this permission is currently active in `userAtom.permissions[]` */
  active: boolean
}

/**
 * Hook encapsulating the permission panel logic for the testing panel.
 *
 * - Captures a one-time snapshot of the user's original permissions on first call.
 * - Exposes entries for both the "User Permissions" and "Page Permissions" blocks.
 * - Both blocks write to the same source of truth (`userAtom.permissions[]`),
 *   so overlapping permissions are automatically kept in sync.
 *
 * @returns userPermissionEntries - All permissions from the initial snapshot with their active state
 * @returns pagePermissionEntries - Permissions declared by the current page via `usePagePermissions`
 * @returns handleTogglePermission - Toggle a permission on/off in `userAtom.permissions[]`
 * @returns hasPagePermissions - Whether the current page declared any permissions
 *
 * @example
 * ```ts
 * const { userPermissionEntries, pagePermissionEntries, handleTogglePermission } =
 *   usePermissionPanel()
 * ```
 */
export const usePermissionPanel = () => {
  const [user, setUser] = useAtom(userAtom)
  const pagePermissions = useAtomValue(pagePermissionsAtom)
  const [snapshot, setSnapshot] = useAtom(permissionsSnapshotAtom)

  // Capture a snapshot of the original permissions the first time the panel is used.
  useEffect(() => {
    if (snapshot === null && user?.permissions) {
      setSnapshot([...user.permissions])
    }
  }, [snapshot, user?.permissions, setSnapshot])

  const permissions = user?.permissions

  /**
   * Adds or removes a raw permission string from `userAtom.permissions[]`.
   * Both the "User Permissions" and "Page Permissions" blocks use this handler,
   * ensuring that shared permissions stay in sync automatically.
   */
  const handleTogglePermission = useCallback(
    (permission: string, enabled: boolean): void => {
      if (!user) return

      const currentPermissions = permissions ?? []

      const next = enabled
        ? [...new Set([...currentPermissions, permission])]
        : currentPermissions.filter(p => p !== permission)

      setUser({ ...user, permissions: next })
    },
    [user, permissions, setUser],
  )

  const userPermissionEntries: UserPermissionEntry[] = (snapshot ?? []).map(permission => ({
    permission,
    active: (permissions ?? []).includes(permission),
  }))

  const pagePermissionEntries: PagePermissionEntry[] = Object.entries(pagePermissions).map(
    ([key, def]) => ({
      key,
      permission: def.permission,
      description: def.description,
      active: (permissions ?? []).includes(def.permission),
    }),
  )

  /**
   * Replaces the full set of active permissions from the user snapshot in one shot.
   * Only permissions that belong to the snapshot are affected; others are preserved.
   */
  const handleSetUserPermissions = useCallback(
    (activePermissions: string[]): void => {
      if (!user) return
      const snapshotSet = new Set(snapshot ?? [])
      const currentPermissions = permissions ?? []
      // Keep permissions not in the snapshot untouched, replace snapshot ones.
      const next = [
        ...currentPermissions.filter(p => !snapshotSet.has(p)),
        ...activePermissions.filter(p => snapshotSet.has(p)),
      ]
      setUser({ ...user, permissions: next })
    },
    [user, permissions, snapshot, setUser],
  )

  return {
    userPermissionEntries,
    pagePermissionEntries,
    handleTogglePermission,
    handleSetUserPermissions,
    hasPagePermissions: pagePermissionEntries.length > 0,
  }
}
